import React, { useState, useEffect } from 'react';
import { Mail, Eye, Reply, Archive, Search, Filter, CheckCircle, Clock, AlertCircle, X, Maximize2, Minimize2 } from 'lucide-react';
import axios from 'axios';
import { useNotifications } from '../../hooks/useNotifications';
import { useConfirm } from '../../contexts/ConfirmContext';
import { useEscapeKey } from '../../hooks/useEscapeKey';
import { useExpandable } from '../../hooks/useExpandable';
import Breadcrumbs from '../../components/Admin/Breadcrumbs';
import { useTranslation } from 'react-i18next';
import { ActionButton, Button, IconButton, Pagination } from '../../components/UI';

interface Contact {
  _id: string;
  name: string;
  email: string;
  phone?: string;
  subject: string;
  message: string;
  status: 'new' | 'read' | 'replied' | 'closed';
  priority: 'low' | 'medium' | 'high';
  country?: string;
  immigrationType?: string;
  isRead: boolean;
  readAt?: string;
  repliedAt?: string;
  replyMessage?: string;
  createdAt: string;
}

const AdminContacts: React.FC = () => {
  const { t } = useTranslation();
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const { isExpanded: isContactModalExpanded, setIsExpanded: setIsContactModalExpanded, toggleExpanded: toggleContactModalExpanded } = useExpandable();
  useEscapeKey(() => { setSelectedContact(null); setIsContactModalExpanded(false); }, !!selectedContact);
  const notifications = useNotifications();
  const confirm = useConfirm();

  const statusOptions = [
    { value: '', label: 'Todos os status' },
    { value: 'new', label: 'Novos' },
    { value: 'read', label: 'Lidos' },
    { value: 'replied', label: 'Respondidos' },
    { value: 'closed', label: 'Fechados' },
  ];

  const priorityOptions = [
    { value: '', label: 'Todas as prioridades' },
    { value: 'low', label: 'Baixa' },
    { value: 'medium', label: 'Média' },
    { value: 'high', label: 'Alta' },
  ];

  useEffect(() => {
    fetchContacts();
  }, [searchTerm, statusFilter, priorityFilter, currentPage]);

  const fetchContacts = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: '10',
        ...(searchTerm && { search: searchTerm }),
        ...(statusFilter && { status: statusFilter }),
        ...(priorityFilter && { priority: priorityFilter }),
      });

      const response = await axios.get(`/contacts?${params}`);
      setContacts(response.data.contacts);
      setTotalPages(response.data.totalPages);
    } catch (error) {
      console.error('Erro ao carregar contatos:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (contactId: string, newStatus: string) => {
    try {
      await axios.put(`/contacts/${contactId}`, {
        status: newStatus
      });
      
      setContacts(contacts.map(contact => 
        contact._id === contactId 
          ? { ...contact, status: newStatus as any }
          : contact
      ));
      
      notifications.success.contactUpdated();
    } catch (error: any) {
      console.error('Erro ao alterar status:', error);
      notifications.error.contactUpdate(error.response?.data?.message || 'Erro ao alterar status');
    }
  };

  const handleDeleteContact = async (contactId: string) => {
    if (!(await confirm({ message: t('admin.contactDeleteConfirm'), confirmText: t('common.delete'), variant: 'danger' }))) return;
    try {
      await axios.delete(`/contacts/${contactId}`);
      setContacts(contacts.filter(contact => contact._id !== contactId));
      notifications.success.contactDeleted();
    } catch (error: any) {
      console.error('Erro ao deletar contato:', error);
      notifications.error.contactDelete(error.response?.data?.message || 'Erro ao deletar contato');
    }
  };

  const getStatusColor = (status: string) => {
    const colors = {
      new: 'bg-orange-100 text-orange-800',
      read: 'bg-blue-100 text-blue-800',
      replied: 'bg-green-100 text-green-800',
      closed: 'bg-gray-100 text-gray-800',
    };
    return colors[status as keyof typeof colors] || colors.new;
  };

  const getPriorityColor = (priority: string) => {
    const colors = {
      low: 'bg-green-100 text-green-800',
      medium: 'bg-yellow-100 text-yellow-800',
      high: 'bg-red-100 text-red-800',
    };
    return colors[priority as keyof typeof colors] || colors.medium;
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'new':
        return <AlertCircle className="w-4 h-4" />;
      case 'read':
        return <Eye className="w-4 h-4" />;
      case 'replied':
        return <Reply className="w-4 h-4" />;
      case 'closed':
        return <CheckCircle className="w-4 h-4" />;
      default:
        return <Clock className="w-4 h-4" />;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
          <div className="mb-4">
            <Breadcrumbs />
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{t('admin.contacts')}</h1>
              <p className="text-gray-600">Visualize e gerencie mensagens recebidas</p>
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-500">
                {contacts.filter(c => c.status === 'new').length} novos
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        {/* Filters */}
        <div className="bg-white rounded-lg shadow mb-6 p-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Search */}
            <div className="md:col-span-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Buscar contatos..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="field pl-10 pr-4"
                />
              </div>
            </div>

            {/* Status Filter */}
            <div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="field field--select"
              >
                {statusOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Priority Filter */}
            <div>
              <select
                value={priorityFilter}
                onChange={(e) => setPriorityFilter(e.target.value)}
                className="field field--select"
              >
                {priorityOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Contacts Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
              <p className="mt-2 text-gray-600">Carregando contatos...</p>
            </div>
          ) : contacts.length === 0 ? (
            <div className="p-8 text-center">
              <div className="text-gray-400 mb-4">
                <Mail className="w-16 h-16 mx-auto" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Nenhum contato encontrado</h3>
              <p className="text-gray-600">
                {searchTerm || statusFilter || priorityFilter 
                  ? 'Tente ajustar seus filtros de busca.'
                  : 'Ainda não há mensagens recebidas.'
                }
              </p>
            </div>
          ) : (
            <>
            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Contato
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Assunto
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Prioridade
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Data
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {contacts.map((contact) => (
                    <tr 
                      key={contact._id} 
                      className={`hover:bg-gray-50 ${!contact.isRead ? 'bg-blue-50' : ''}`}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            <div className="h-10 w-10 bg-primary-100 rounded-full flex items-center justify-center">
                              <span className="text-primary-600 font-medium text-sm">
                                {contact.name.charAt(0).toUpperCase()}
                              </span>
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {contact.name}
                            </div>
                            <div className="text-sm text-gray-500">
                              {contact.email}
                            </div>
                            {contact.phone && (
                              <div className="text-sm text-gray-500">
                                {contact.phone}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900 font-medium">
                          {contact.subject}
                        </div>
                        <div className="text-sm text-gray-500 line-clamp-1">
                          {contact.message}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-2">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full flex items-center space-x-1 ${getStatusColor(contact.status)}`}>
                            {getStatusIcon(contact.status)}
                            <span>{contact.status}</span>
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getPriorityColor(contact.priority)}`}>
                          {contact.priority}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(contact.createdAt)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          <IconButton action="view" label="Ver detalhes" onClick={() => setSelectedContact(contact)} />
                          
                          {contact.status !== 'replied' && (
                            <button
                              onClick={() => handleStatusChange(contact._id, 'replied')}
                              className="p-2 text-green-600 hover:bg-green-100 rounded-lg transition-colors duration-200"
                              title="Marcar como respondido"
                            >
                              <Reply className="w-4 h-4" />
                            </button>
                          )}
                          
                          {contact.status !== 'closed' && (
                            <button
                              onClick={() => handleStatusChange(contact._id, 'closed')}
                              className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                              title="Fechar"
                            >
                              <Archive className="w-4 h-4" />
                            </button>
                          )}
                          
                          <IconButton action="delete" onClick={() => handleDeleteContact(contact._id)} />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="md:hidden space-y-4 p-4">
              {contacts.map((contact) => (
                <div
                  key={contact._id}
                  className={`rounded-lg shadow-sm border border-gray-200 p-4 ${!contact.isRead ? 'bg-blue-50' : 'bg-white'}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="flex-shrink-0 h-10 w-10 bg-primary-100 rounded-full flex items-center justify-center">
                        <span className="text-primary-600 font-medium text-sm">
                          {contact.name.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{contact.name}</p>
                        <p className="text-sm text-gray-500 truncate">{contact.email}</p>
                        {contact.phone && (
                          <p className="text-sm text-gray-500 truncate">{contact.phone}</p>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="mb-3">
                    <p className="text-sm text-gray-900 font-medium">{contact.subject}</p>
                    <p className="text-sm text-gray-500 line-clamp-2">{contact.message}</p>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                    <div>
                      <span className="text-gray-500">Status:</span>
                      <p>
                        <span className={`inline-flex items-center gap-1 mt-0.5 px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(contact.status)}`}>
                          {getStatusIcon(contact.status)}
                          <span>{contact.status}</span>
                        </span>
                      </p>
                    </div>
                    <div>
                      <span className="text-gray-500">Prioridade:</span>
                      <p>
                        <span className={`inline-block mt-0.5 px-2 py-1 text-xs font-medium rounded-full ${getPriorityColor(contact.priority)}`}>
                          {contact.priority}
                        </span>
                      </p>
                    </div>
                    <div className="col-span-2">
                      <span className="text-gray-500">Data:</span>
                      <p className="font-medium text-gray-900">{formatDate(contact.createdAt)}</p>
                    </div>
                  </div>
                  <div className="row-actions mt-3">
                    <ActionButton
                      action="view"
                      onClick={() => setSelectedContact(contact)}
                    />
                    {contact.status !== 'replied' && (
                      <ActionButton
                        icon={Reply}
                        label="Respondido"
                        variant="success"
                        onClick={() => handleStatusChange(contact._id, 'replied')}
                      />
                    )}
                    {contact.status !== 'closed' && (
                      <ActionButton
                        icon={Archive}
                        label="Fechar"
                        onClick={() => handleStatusChange(contact._id, 'closed')}
                      />
                    )}
                    <ActionButton
                      action="delete"
                      onClick={() => handleDeleteContact(contact._id)}
                    />
                  </div>
                </div>
              ))}
            </div>
            </>
          )}

          <Pagination currentPage={currentPage} totalPages={totalPages} onChange={setCurrentPage} />
        </div>
      </div>

      {/* Contact Detail Modal */}
      {selectedContact && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4 overflow-y-auto">
          <div className={`w-full p-5 border shadow-lg rounded-t-2xl sm:rounded-md bg-white overflow-y-auto transition-all duration-200 ${
            isContactModalExpanded ? 'max-w-[95vw] max-h-[95vh]' : 'sm:max-w-2xl max-h-[85vh]'
          }`}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-gray-900">Detalhes do Contato</h3>
              <div className="flex items-center gap-1">
                <button
                  onClick={toggleContactModalExpanded}
                  className="icon-btn text-gray-400 hover:text-gray-600 p-1"
                  title={isContactModalExpanded ? 'Restaurar' : 'Expandir'}
                >
                  {isContactModalExpanded ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}
                </button>
                <button
                  onClick={() => { setSelectedContact(null); setIsContactModalExpanded(false); }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <span className="sr-only">Fechar</span>
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            <div className="space-y-4">
              <div>
                <h4 className="font-semibold text-gray-900">Informações do Contato</h4>
                <div className="mt-2 space-y-2">
                  <p><strong>Nome:</strong> {selectedContact.name}</p>
                  <p><strong>Email:</strong> {selectedContact.email}</p>
                  {selectedContact.phone && <p><strong>Telefone:</strong> {selectedContact.phone}</p>}
                  {selectedContact.country && <p><strong>País:</strong> {selectedContact.country}</p>}
                  {selectedContact.immigrationType && <p><strong>Tipo de Imigração:</strong> {selectedContact.immigrationType}</p>}
                </div>
              </div>
              
              <div>
                <h4 className="font-semibold text-gray-900">Mensagem</h4>
                <div className="mt-2">
                  <p><strong>Assunto:</strong> {selectedContact.subject}</p>
                  <p className="mt-2 text-gray-700">{selectedContact.message}</p>
                </div>
              </div>
              
              <div>
                <h4 className="font-semibold text-gray-900">Status</h4>
                <div className="mt-2 flex items-center space-x-4">
                  <span className={`px-3 py-1 text-sm font-medium rounded-full ${getStatusColor(selectedContact.status)}`}>
                    {selectedContact.status}
                  </span>
                  <span className={`px-3 py-1 text-sm font-medium rounded-full ${getPriorityColor(selectedContact.priority)}`}>
                    {selectedContact.priority}
                  </span>
                </div>
              </div>
            </div>
            
            <div className="form-actions mt-6">
              <Button variant="ghost" size="sm"
                onClick={() => { setSelectedContact(null); setIsContactModalExpanded(false); }}>
                Fechar
              </Button>
              <Button
                onClick={() => {
                  handleStatusChange(selectedContact._id, 'replied');
                  setSelectedContact(null);
                  setIsContactModalExpanded(false);
                }}>
                Marcar como Respondido
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminContacts;
